import { FileCheck2 } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../api';
import { useAccess } from '../../lib/access';
import { useApi } from '../../lib/useApi';
import { hubUrl } from '../../nav/hubs';

const READ = ['dossier.review', 'compliance.read', 'work_item.read'];
const REFRESH_MS = 60_000;

/**
 * How many domain dossiers need a manual review, refreshed every minute; 0 for users who cannot
 * read the queue or when the count cannot be loaded. Shared by the top-bar badge and the sidebar.
 */
export function useDossierReviewCount(): number {
  const access = useAccess();
  const allowed = !access.client && access.has(READ);
  const summary = useApi((signal) => (allowed ? api.dossierQueueSummary({ signal }) : Promise.resolve(null)), [allowed]);
  const { reload } = summary;
  useEffect(() => {
    if (!allowed) return undefined;
    const id = window.setInterval(reload, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [allowed, reload]);
  return allowed && summary.status === 'success' && summary.data ? summary.data.needs_review : 0;
}

/**
 * Top-bar badge: how many domain dossiers need a manual review ("cần duyệt tay"), refreshed
 * every minute; opens the "Hồ sơ tên miền" queue filtered to them. Hidden when there are none,
 * for users who cannot read the queue, and when the count cannot be loaded.
 */
export function DossierBadge() {
  const { t } = useTranslation('dossiers');
  const n = useDossierReviewCount();
  if (n <= 0) return null;
  const label = t('badge', { count: n });
  return (
    <Link
      to={hubUrl('home', 'dossiers', undefined, { stage: 'NEEDS_REVIEW' })}
      className="inline-flex h-8 items-center gap-1 rounded-md border border-serious/30 bg-serious-soft px-2 text-xs font-semibold text-serious hover:bg-serious/15"
      title={label}
      aria-label={label}
    >
      <FileCheck2 className="size-4" aria-hidden="true" />
      <span className="tabular">{n > 99 ? '99+' : n}</span>
    </Link>
  );
}
